import { unitSymbol, resolveQuantityColumns } from '@/lib/mappers/units';
import {
  DECLARATION_FILE_ACCEPT,
  MAX_DECLARATION_FILE_SIZE_BYTES,
  MAX_DECLARATION_FILES,
} from '../constants/declarations.constants';
import {
  GHG_DEFAULT_VALUE,
  MULTISITE_TRANSFER_VALUE,
} from '../constants/validateDeclaration.constants';

// Server enums are lowercase ("yes"/"no"); the form's own enums are uppercase — one place to cross.
export const toBackendYesNo = (value) => {
  if (value === GHG_DEFAULT_VALUE.yes || value === MULTISITE_TRANSFER_VALUE.yes) return 'yes';
  if (value === GHG_DEFAULT_VALUE.no || value === MULTISITE_TRANSFER_VALUE.no) return 'no';
  return undefined;
};

export const fromBackendYesNo = (value) => {
  if (value === 'yes') return GHG_DEFAULT_VALUE.yes;
  if (value === 'no') return GHG_DEFAULT_VALUE.no;
  return '';
};

// GraphQL types multiSiteTransfer as a Boolean, unlike the yes/no strings REST sends back.
export const fromBackendBoolean = (value) =>
  value == null ? '' : value ? MULTISITE_TRANSFER_VALUE.yes : MULTISITE_TRANSFER_VALUE.no;

// Basic Information locks (and Link shipment hides) once the batch came in via a linked shipment.
export const isDeclarationLocked = (node) => (node?.shipmentIds ?? []).length > 0;

/**
 * The material + unit symbols + working/tracking columns <ValidateDeclarationQuantityFields>
 * and the Summary step both derive from `materialId`/`quantity` — one place for the lookup.
 */
export const resolveDeclarationQuantityColumns = ({
  materials,
  materialId,
  quantity,
  conversion = null,
}) => {
  const material = materials.find((item) => item.id === materialId) ?? null;
  const declarationUnit = material ? unitSymbol(material.declarationUnit) : '';
  const workingUnit = material?.workingUnit ? unitSymbol(material.workingUnit) : '';
  const trackingUnit = material?.trackingUnit ? unitSymbol(material.trackingUnit) : '';
  // The quantity is always entered in the declaration unit, so that column mirrors it.
  const columns = resolveQuantityColumns({
    entered: quantity,
    unit: material?.declarationUnit,
    material,
    trackingUnit: material?.trackingUnit,
    conversion,
  });
  return { material, declarationUnit, workingUnit, trackingUnit, columns };
};

// Floating-point tolerance for comparing the entered Total GHG against the computed breakdown.
const GHG_TOTAL_TOLERANCE = 0.01;

const toNumber = (value) => (value === '' || value == null ? 0 : Number(value));

// Total = Eec + El + Ep + Etd + Eu - Esca - Eccs - Eccr (RED III); every term is optional.
export const computeGhgTotalFromBreakdown = (values) =>
  toNumber(values.eec) +
  toNumber(values.el) +
  toNumber(values.ep) +
  toNumber(values.etd) +
  toNumber(values.eu) -
  toNumber(values.escaValue) -
  toNumber(values.eccs) -
  toNumber(values.eccr);

export const ghgTotalMatchesBreakdown = (totalGhgEmissions, values) =>
  Math.abs(toNumber(totalGhgEmissions) - computeGhgTotalFromBreakdown(values)) <=
  GHG_TOTAL_TOLERANCE;

// Pure/no-React layer — returns error CODES, not copy; SummaryStep translates them via t().
export const DeclarationFileErrorCode = Object.freeze({
  invalidFormat: 'invalidFormat',
  tooLarge: 'tooLarge',
  duplicate: 'duplicate',
});

const isPdf = (file) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith(DECLARATION_FILE_ACCEPT);

export const getFileErrorCode = (file, existingNames) => {
  if (!isPdf(file)) return DeclarationFileErrorCode.invalidFormat;
  if (file.size > MAX_DECLARATION_FILE_SIZE_BYTES) return DeclarationFileErrorCode.tooLarge;
  if (existingNames.has(file.name.toLowerCase())) return DeclarationFileErrorCode.duplicate;
  return null;
};

let nextFileEntryId = 0;

// existingNames is mutated per-batch, so duplicates within the same drop are caught too.
export const createFileEntry = (file, existingNames) => {
  nextFileEntryId += 1;
  return {
    id: `${file.name}-${file.lastModified}-${file.size}-${nextFileEntryId}`,
    name: file.name,
    size: file.size,
    errorCode: getFileErrorCode(file, existingNames),
    file, // the File itself — uploaded by buildDeclarationsFormData, previewed by openFile
  };
};

// Flags the names the account already holds, matched as the server matches them (trimmed, any case).
export const markTakenFiles = (files, takenNames) => {
  if (!takenNames?.length) return files;
  const taken = new Set(takenNames.map((name) => name.trim().toLowerCase()));
  return files.map((entry) =>
    !entry.errorCode && taken.has(entry.name.trim().toLowerCase())
      ? { ...entry, errorCode: DeclarationFileErrorCode.duplicate }
      : entry,
  );
};

// Appends a picked batch. An errored file is kept so the review step can report it.
export const addFileEntries = (existing, fileList, max = MAX_DECLARATION_FILES) => {
  if (existing.length >= max) return existing;
  const incoming = Array.from(fileList ?? []).slice(0, max - existing.length);
  const existingNames = new Set(existing.map((entry) => entry.name.toLowerCase()));

  return [
    ...existing,
    ...incoming.map((file) => {
      const entry = createFileEntry(file, existingNames);
      existingNames.add(entry.name.toLowerCase());
      return entry;
    }),
  ];
};

// The Calculate emissions modal's origin dropdown — the supplier's sites that reach this one.
export const buildEmissionShippingOptions = (routes, receivedAtSiteId) => {
  const seen = new Set();
  return (routes ?? [])
    .filter((route) => !receivedAtSiteId || route.sentToSiteId === receivedAtSiteId)
    .filter((route) => route.shippingFromId && !seen.has(route.shippingFromId))
    .map((route) => {
      seen.add(route.shippingFromId);
      return { value: route.shippingFromId, label: route.shippingFromName };
    });
};

// Its route dropdown, narrowed to the chosen origin.
export const buildEmissionRouteOptions = (routes, shippingFromId) =>
  (routes ?? [])
    .filter((route) => route.shippingFromId === String(shippingFromId))
    .map((route) => ({ value: route.id, label: route.name }));
